import { useCallback, useEffect, useState } from "react";
import { Lock, Plus, Trash2, Loader2 } from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ConsultantNotes() {
  const { company, companyId, period } = useTenant();
  const [notes, setNotes] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/consultant-notes`);
      setNotes(data);
    } catch {
      setNotes([]);
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/consultant-notes`,
        { title: title.trim(), body: body.trim() });
      toast.success("Nota interna salvata (visibile solo ai consulenti)");
      setTitle("");
      setBody("");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (note) => {
    if (!window.confirm(`Eliminare la nota "${note.title}"?`)) return;
    try {
      await api.delete(`/companies/${companyId}/periods/${period.id}/consultant-notes/${note.id}`);
      toast.success("Nota eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="consultant-notes-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          Note interne consulente
          <Lock className="w-5 h-5 text-amber-500" aria-hidden="true" />
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          <strong>{company?.denominazione}</strong> · Esercizio {period?.year} — queste note sono
          <strong> CONSULTANT_ONLY</strong>: mai visibili agli utenti dell'azienda.
        </p>
      </div>

      <Card className="border-slate-200" data-testid="note-form-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Plus className="w-4 h-4" aria-hidden="true" /> Nuova nota
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={add} className="space-y-3" data-testid="note-form">
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Titolo (es. Verifica qualità dati energia)" maxLength={200}
              className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
              data-testid="note-title-input" aria-label="Titolo nota" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Appunti professionali, valutazioni, promemoria per il report…" maxLength={5000} rows={3}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
              data-testid="note-body-input" aria-label="Testo nota" />
            <Button type="submit" size="sm" disabled={saving || !title.trim() || !body.trim()}
              className="bg-green-700 hover:bg-green-800 text-white" data-testid="note-submit">
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" aria-hidden="true" />}
              Salva nota interna
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700">
            Note del periodo {notes ? `(${notes.length})` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notes === null ? (
            <p className="text-sm text-slate-500">Caricamento…</p>
          ) : notes.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="notes-empty">Nessuna nota interna per questo periodo.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="notes-list">
              {notes.map((n) => (
                <li key={n.id} className="py-3 flex items-start gap-3" data-testid={`note-row-${n.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{n.title}</p>
                    <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{n.body}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {n.created_by_name || "Consulente"} · {new Date(n.created_at).toLocaleString("it-IT")}
                    </p>
                  </div>
                  <Button size="sm" variant="outline"
                    className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => remove(n)} data-testid={`note-delete-${n.id}`}>
                    <Trash2 className="w-3 h-3" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
